import { Check, ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from './ui/badge';
import { Card } from './ui/card';

interface Service {
  icon: string;
  title: string;
  description: string;
  features: string[];
  link?: string;
  highlighted?: boolean;
}

const services: Service[] = [
  {
    icon: '⚙️',
    title: 'Pneuservis',
    description: 'Profesionálna výmena pneumatík s automatickou prezúvačkou a 3D vyvažovačkou. Sezónne uskladnenie.',
    features: [
      '3D vyvažovačka CB78P s diagnostikou hádzania',
      'Automatická prezúvačka LC897V (12"-30")',
      'Sonarová detekcia deformácií a problémov',
      'Sezónne uskladnenie pneumatík v Trenčíne',
    ],
    link: '/sluzby/pneuservis-trencin',
    highlighted: true,
  },
  {
    icon: '⚡',
    title: '3D Geometria',
    description: 'Hunter HawkEye Elite s patentovanou 3D technológiou. Presnosť na 0,01° s databázou 32,000+ vozidiel.',
    features: [
      'Hunter HawkEye Elite 3D geometria',
      'Kamery so 4-násobným rozlíšením',
      'Databáza 32,000+ vozidiel',
      'Ľahké terče 2.7 kg, adaptéry do 37"',
    ],
    link: '/sluzby/geometria-trencin',
    highlighted: true,
  },
  {
    icon: '📡',
    title: 'ADAS Kalibrácia',
    description: 'TOPDON Phoenix ADAS Mobile - mobilná kalibračná sada. Povinná po výmene skla a nárazoch.',
    features: [
      'TOPDON Phoenix ADAS Mobile kalibrácia',
      'Kalibrácia kamier, radarov a senzorov',
      'ACC, LDW, AEB, BSD, AVM, NVS systémy',
      'Statická aj dynamická kalibrácia',
    ],
    link: '/sluzby/adas',
  },
  {
    icon: '🔧',
    title: 'Komplexný Autoservis',
    description: 'Servis všetkých značiek so špecializáciou na BMW, Porsche, Mercedes, Audi. Certifikovaní mechanici.',
    features: [
      'Servis všetkých značiek vozidiel',
      'Pravidelná údržba a servisné prehliadky',
      'Výmena oleja, filtrov a brzdových dosiek',
      'Použitie originálnych náhradných dielov',
    ],
    link: '/booking',
  },
  {
    icon: '🔬',
    title: 'Počítačová Diagnostika',
    description: 'TOPDON Phoenix Max s 13,3" displejom a 4-kanálovým osciloskopom. J2534 online programovanie ECU.',
    features: [
      'TOPDON Phoenix Max s 13,3" displejom',
      'Bezdrôtový MDCI Pro + 4-kanálový osciloskop',
      'J2534 online programovanie ECU',
      'Podpora nákladných vozidiel zadarmo',
    ],
    link: '/sluzby/diagnostika-trencin',
  },
  {
    icon: '🔄',
    title: 'Náhradné Vozidlá ZDARMA',
    description: 'Bezplatné náhradné vozidlá pri poistnej udalosti (100% hradené poisťovňou). Vybavenie do 24 hodín.',
    features: [
      '100% ZDARMA - hradené poisťovňou',
      'Kvalitné náhradné vozidlá (Audi, Škoda, Mercedes)',
      'Rýchle vybavenie do 24 hodín',
      'Dostupné v celom západnom Slovensku',
    ],
    link: '/replacement-vehicles',
  },
  {
    icon: '✨',
    title: 'Auto Detailing & Car Care',
    description: 'Korekcia a leštenie laku, keramická ochrana, hĺbkové čistenie interiéru. Špecializácia na luxus.',
    features: [
      'Exteriérové čistenie a leštenie laku',
      'Čistenie a impregnácia interiéru vozidla',
      'Keramická ochrana laku proti poveternostným vplyvom',
      'Ochrana interiéru - kože, textilií a obšitia',
    ],
    link: '/sluzby/detailing',
  },
];

export function Services() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <section className="relative py-16 overflow-hidden bg-black">
      {/* Background effects */}
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-lime-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-lime-600/5 rounded-full blur-3xl" />
      
      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `linear-gradient(to right, rgb(255 255 255 / 0.1) 1px, transparent 1px),
                          linear-gradient(to bottom, rgb(255 255 255 / 0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />

      <div className="container mx-auto px-6 relative z-10">
        
        {/* Section header */}
        <div className="text-center mb-12">
          <Badge 
            variant="secondary"
            className="mb-6 px-6 py-2 bg-zinc-900/50 border-lime-500/20 hover:border-lime-500/40"
          >
            <span className="text-sm font-medium text-lime-400">Naše služby v Trenčíne</span>
          </Badge>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Autoservis, Pneuservis a Geometria
            <span className="block mt-3 bg-gradient-to-r from-lime-400 to-lime-300 bg-clip-text text-transparent">
              v Trenčíne
            </span>
          </h2>
          
          <p className="text-xl text-zinc-400 max-w-3xl mx-auto leading-relaxed">
            Komplexné služby autoservisu v Trenčíne: pneuservis s uskladnením, 3D geometria, 
            ADAS kalibrácia, diagnostika TOPDON a servis všetkých značiek vozidiel.
          </p>
        </div>

        {/* Modern services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {services.map((service, index) => (
            <Card
              key={index}
              className={`group relative overflow-hidden bg-zinc-900/50 backdrop-blur-sm border-zinc-800 hover:border-lime-500/40 transition-all duration-500 cursor-pointer ${
                hoveredIndex === index ? 'scale-[1.02] shadow-xl shadow-lime-500/10' : ''
              } ${service.highlighted ? 'border-lime-500/30' : ''}`}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className="p-8">
                
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="text-5xl group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  {service.highlighted && (
                    <Badge className="bg-lime-400 text-black hover:bg-lime-400 text-xs font-semibold">
                      Top
                    </Badge>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-lime-400 transition-colors">
                  {service.title}
                </h3>

                {/* Description */}
                <p className="text-zinc-400 mb-6 leading-relaxed text-sm line-clamp-3">
                  {service.description}
                </p>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <Check className="w-5 h-5 text-lime-400 flex-shrink-0 mt-0.5" />
                      <span className="text-zinc-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <div className="pt-6 border-t border-zinc-800">
                  <Link 
                    to={service.link || '/booking'}
                    className="inline-flex items-center gap-2 text-lime-400 hover:text-lime-300 font-medium group/link transition-colors"
                  >
                    <span>Objednať</span>
                    <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>

              {/* Hover gradient effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-lime-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
